import { useState } from 'react';

interface TreatmentModalProps {
  treatment: (typeof treatments)[number] | null;
  onClose: () => void;
}

const treatments = [
  {
    id: 'protesis',
    title: 'Prótesis',
    subtitle: 'Restauración de órganos dentales',
    accentColor: '#C9A84C',
    image:
      'https://readdy.ai/api/search-image?query=dental%20prosthesis%20removable%20denture%203d%20render%20realistic%20teeth%20gums%20pink%20white%20black%20background%20professional%20medical%20illustration%20high%20quality&width=480&height=300&seq=mftreat01&orientation=landscape',
    short:
      'Es el área de la odontología que se encarga de reemplazar uno o más órganos dentales como consecuencia de su pérdida o ausencia en boca.',
    full: 'Es el área de la odontología que se encarga de reemplazar uno o más órganos dentales como consecuencia de su pérdida o ausencia en boca. Existen varios tipos de prótesis (fijas y removibles), por lo que es importante guiarte y recomendarte cuál podría ser tu mejor opción. Una valoración previa es fundamental para determinar el tratamiento más adecuado según tu caso específico.',
    bullets: ['Prótesis fija', 'Prótesis removible', 'Prótesis sobre implantes', 'Corona y puente'],
  },
  {
    id: 'aclaramiento',
    title: 'Aclaramiento Dental',
    subtitle: 'Blanqueamiento profesional',
    accentColor: '#3A86FF',
    image:
      'https://readdy.ai/api/search-image?query=dental%20teeth%20whitening%20bleaching%20tray%203d%20render%20blue%20scanner%20professional%20dental%20illustration%20upper%20jaw%20clinical%20high%20quality%20realistic&width=480&height=300&seq=mftreat02&orientation=landscape',
    short:
      'Es un procedimiento dental que se encarga de aclarar los dientes mediante un proceso químico para obtener resultados estéticos visibles.',
    full: 'Es un procedimiento dental que se encarga de aclarar los dientes mediante un proceso químico para obtener resultados estéticos. Existen varios tipos de aclaramientos dentales, por lo cual siempre es indispensable realizar una valoración previa. El tratamiento puede realizarse en consultorio para resultados inmediatos, o con férulas a domicilio bajo supervisión profesional.',
    bullets: [
      'Blanqueamiento en consultorio',
      'Blanqueamiento con férulas',
      'Evaluación previa obligatoria',
      'Resultados desde la 1.ª sesión',
    ],
  },
  {
    id: 'ortodoncia',
    title: 'Ortodoncia',
    subtitle: 'Corrección y alineación dental',
    accentColor: '#C9A84C',
    image:
      'https://readdy.ai/api/search-image?query=dental%20orthodontics%20metal%20braces%20brackets%20teeth%20upper%20jaw%20close%20up%20realistic%203d%20render%20pink%20gums%20white%20teeth%20professional%20medical%20illustration&width=480&height=300&seq=mftreat03&orientation=landscape',
    short:
      'Es el área de la odontología que se encarga de la corrección de la posición de los dientes en relación con los huesos craneofaciales.',
    full: 'Es el área de la odontología que se encarga de la corrección de la posición de los dientes en relación con los huesos craneofaciales, con el fin de lograr una armonía estética, fonética y miofuncional, mediante la colocación de diferentes aditamentos como brackets, arcos, cadenas y aparatos. Para ello, es importante realizar una valoración previa y guiarte hacia tu mejor opción de tratamiento.',
    bullets: [
      'Brackets metálicos',
      'Brackets de cerámica',
      'Alineadores transparentes',
      'Férulas de retención',
    ],
  },
  {
    id: 'implantes',
    title: 'Implantes Dentales',
    subtitle: 'Solución permanente ante la pérdida dental',
    accentColor: '#3A86FF',
    image:
      'https://readdy.ai/api/search-image?query=dental%20implant%20titanium%20screw%20jaw%20bone%203d%20render%20realistic%20cross%20section%20anatomy%20white%20background%20medical%20illustration%20professional%20high%20quality&width=480&height=300&seq=mftreat04&orientation=landscape',
    short:
      'Los implantes dentales son la solución más avanzada para reemplazar dientes perdidos, integrándose al hueso mandibular para una fijación natural.',
    full: 'Los implantes dentales son raíces artificiales de titanio que se colocan en el hueso maxilar o mandibular para sustituir dientes perdidos. Sobre ellos se coloca una corona, puente o prótesis. Al integrarse con el hueso (osteointegración), ofrecen la solución más estable, duradera y estética disponible en odontología moderna. Una evaluación previa es indispensable para verificar la densidad ósea y condiciones del paciente.',
    bullets: [
      'Implante unitario',
      'Implantes múltiples',
      'Prótesis sobre implantes',
      'All-on-4 / All-on-6',
    ],
  },
  {
    id: 'endodoncia',
    title: 'Endodoncia',
    subtitle: 'Tratamiento de conductos radiculares',
    accentColor: '#C9A84C',
    image:
      'https://readdy.ai/api/search-image?query=dental%20root%20canal%20endodontics%20treatment%20tooth%20cross%20section%203d%20render%20realistic%20anatomy%20nerve%20pulp%20professional%20medical%20illustration%20high%20quality&width=480&height=300&seq=mftreat05&orientation=landscape',
    short:
      'La endodoncia consiste en el tratamiento del interior del diente (conductos radiculares) para eliminar infecciones y preservar el órgano dental.',
    full: 'La endodoncia o tratamiento de conductos es el procedimiento que elimina la pulpa dental infectada o dañada, limpia y da forma a los conductos radiculares, y los sella para prevenir futuras infecciones. Es la alternativa a la extracción del diente, permitiendo conservar el órgano dental natural. Se realiza bajo anestesia local y generalmente no produce dolor durante el procedimiento.',
    bullets: [
      'Tratamiento de conductos',
      'Re-tratamiento endodóntico',
      'Apicectomía',
      'Preservación del diente natural',
    ],
  },
];

function TreatmentModal({ treatment, onClose }: TreatmentModalProps) {
  if (!treatment) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#0A2540]/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-white rounded-3xl w-full max-w-xl overflow-hidden shadow-[0_30px_80px_rgba(10,37,64,0.35)]">
        {/* Image */}
        <div className="relative w-full" style={{ height: '220px' }}>
          <img
            src={treatment.image}
            alt={treatment.title}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/70 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full text-white transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <div className="absolute bottom-4 left-6">
            <p className="text-white/60 text-xs font-medium uppercase tracking-widest mb-1">
              {treatment.subtitle}
            </p>
            <h2 className="text-white font-extrabold text-2xl">{treatment.title}</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-7">
          <p className="text-gray-600 text-sm leading-relaxed mb-6">{treatment.full}</p>
          <div className="mb-7">
            <p className="text-[#0A2540] font-semibold text-sm mb-3">Opciones disponibles:</p>
            <ul className="grid grid-cols-2 gap-2">
              {treatment.bullets.map((b) => (
                <li key={b} className="flex items-center gap-2 text-sm text-gray-600">
                  <div
                    className="w-5 h-5 flex items-center justify-center rounded-md flex-shrink-0"
                    style={{ backgroundColor: treatment.accentColor + '25' }}
                  >
                    <i className="ri-check-line text-xs" style={{ color: treatment.accentColor }} />
                  </div>
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <a
            href="tel:5534748982"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-sm text-white whitespace-nowrap cursor-pointer transition-all hover:opacity-90"
            style={{ backgroundColor: treatment.accentColor }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line" />
            </div>
            Agendar consulta — 55 3474 8982
          </a>
        </div>
      </div>
    </div>
  );
}

export default function TreatmentCardsSection() {
  const [selected, setSelected] = useState<(typeof treatments)[number] | null>(null);

  return (
    <>
      <section id="tratamientos" className="py-32 bg-[#EEF4FF] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16 scroll-animate">
            <span className="inline-block text-[#3A86FF] font-semibold text-sm tracking-widest uppercase mb-3">
              Especialidades
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#0A2540] mb-5">
              Nuestros{' '}
              <span className="bg-gradient-to-r from-[#3A86FF] to-[#0A2540] bg-clip-text text-transparent">
                Tratamientos
              </span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto font-light leading-relaxed">
              Contamos con todas las especialidades para cuidar tu salud bucal de forma integral,
              con tecnología moderna y especialistas certificados.
            </p>
          </div>

          {/* Cards grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {treatments.map((t, i) => (
              <div
                key={t.id}
                className={`group bg-white rounded-3xl overflow-hidden border border-white hover:border-gray-200 transition-all duration-300 hover:-translate-y-2 cursor-pointer scroll-animate anim-delay-${i * 100} flex flex-col`}
                style={{ boxShadow: '0 4px 24px rgba(10,37,64,0.08)' }}
                onClick={() => setSelected(t)}
              >
                {/* Image */}
                <div className="relative overflow-hidden" style={{ height: '210px' }}>
                  <img
                    src={t.image}
                    alt={`Tratamiento dental ${t.title} - MF Dental`}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  {/* Specialty tag */}
                  <div className="absolute top-4 left-4">
                    <span
                      className="text-white text-xs font-semibold px-3 py-1 rounded-full"
                      style={{ backgroundColor: t.accentColor }}
                    >
                      {t.subtitle}
                    </span>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6 flex flex-col flex-1">
                  <h3
                    className="font-extrabold text-xl mb-3 tracking-wide uppercase"
                    style={{ color: t.accentColor }}
                  >
                    {t.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed flex-1">
                    {t.short}
                  </p>
                </div>

                {/* Button */}
                <button
                  className="mx-6 mb-6 flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-sm text-white whitespace-nowrap cursor-pointer transition-all hover:opacity-90 active:scale-95"
                  style={{ backgroundColor: t.accentColor }}
                  onClick={(e) => { e.stopPropagation(); setSelected(t); }}
                >
                  Leer Más
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-circle-line text-base" />
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detail modal */}
      {selected && (
        <TreatmentModal treatment={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}
